import { useEffect, useState, useRef } from "react";
import { findLinkedinMessage, findSMV } from "../../api/ModelsApi";
import {
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  Group,
  Avatar,
  Box,
  Loader, // N'oubliez pas l'import
} from "@mantine/core";
import {
  IconPlayerPlayFilled,
  IconPlayerPauseFilled,
} from "@tabler/icons-react";
import { useLanguage } from "../../stores/LanguageStore";
import { findFile } from "../../api/FIleAPI";

const MessageVocalPreviewer = ({ model_id, file_id, mah }) => {
  const lang = useLanguage((s) => s.language);
  const [modelProfile, setModelProfile] = useState({ file_id });
  const [attachedFile, setAttachedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- NOUVEAUX ÉTATS POUR L'AUDIO ---
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Nouvel état pour savoir si les métadonnées sont chargées
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  useEffect(() => {
    if (model_id) {
      setLoading(true);

      findLinkedinMessage(model_id)
        .then((response) => {
          setModelProfile(response.data.model_profile);
        })
        .finally(() => setLoading(false));
    }
  }, [model_id]);

  useEffect(() => {
    if (modelProfile?.file_id || file_id) {
      findFile(modelProfile?.file_id || file_id).then((fileRes) => {
        setAttachedFile(fileRes.data.file_profile);
      });
    }
  }, [modelProfile?.file_id, file_id]);

  // --- LOGIQUE DU LECTEUR AUDIO ---
  const togglePlay = () => {
    if (!audioRef.current || !attachedFile?.file_url || !isMetadataLoaded)
      return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      // Pour éviter les valeurs Infinity parfois renvoyées par Chrome au premier chargement
      if (audioRef.current.duration !== Infinity) {
        setDuration(audioRef.current.duration);
        setIsMetadataLoaded(true);
      }
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  // Formatage du temps en M:SS
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds < 0) return "0:00";
    const m = Math.floor(timeInSeconds / 60);
    const s = Math.floor(timeInSeconds % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // Calcul du temps affiché (temps restant)
  const timeRemaining = Math.max(0, duration - currentTime);

  // Calcul du pourcentage de la barre de progression
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Stack
      pos={"relative"}
      gap={"sm"}
      flex={1}
      w={"100%"}
      mah={mah}
      style={{
        border: "1px solid #00000018",
        overflow: "auto",
        borderRadius: 12,
      }}
      px={12}
      pb={12}
    >
      <LoadingOverlay visible={loading} />

      {/* Élément audio natif caché */}
      {attachedFile?.file_url && (
        <audio
          ref={audioRef}
          src={attachedFile.file_url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          preload="metadata"
        />
      )}

      <Group wrap="nowrap" align="flex-end" justify="flex-end" mt={16}>
        <Stack gap={4} align="flex-end" style={{ flex: 1 }}>
          <Paper
            bg={"#0a66c2"}
            radius={"md"}
            p={"xs"}
            w={"fit-content"}
            maw={"100%"}
          >
            <Group wrap="nowrap" gap="xs" align="center">
              <Box
                onClick={togglePlay}
                style={{
                  cursor: isMetadataLoaded ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  opacity: isMetadataLoaded ? 1 : 0.5,
                }}
              >
                {isPlaying ? (
                  <IconPlayerPauseFilled size={20} color="white" />
                ) : (
                  <IconPlayerPlayFilled size={20} color="white" />
                )}
              </Box>

              <Box w={1} h={24} bg="rgba(255, 255, 255, 0.3)" mx={4} />

              {/* Barre de progression dynamique */}
              <Box
                flex={1}
                h={4}
                miw={80}
                bg="rgba(255, 255, 255, 0.3)"
                style={{ borderRadius: 4, position: "relative" }}
              >
                <Box
                  w={`${progressPercentage}%`}
                  h="100%"
                  bg="white"
                  style={{ borderRadius: 4, transition: "width 0.1s linear" }}
                />
                <Box
                  w={8}
                  h={8}
                  bg="white"
                  style={{
                    borderRadius: "50%",
                    position: "absolute",
                    top: -2,
                    left: `${progressPercentage}%`,
                    transform: "translateX(-50%)",
                    transition: "left 0.1s linear",
                  }}
                />
              </Box>

              {/* Affichage conditionnel : Loader ou Temps restant */}
              <Box
                style={{
                  minWidth: 32,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
                ml={4}
              >
                {!isMetadataLoaded && attachedFile?.file_url ? (
                  <Loader type="dots" color="white" size="sm" />
                ) : (
                  <Text
                    c="white"
                    size="sm"
                    fw={600}
                    style={{
                      letterSpacing: "0.5px",
                      textAlign: "right",
                    }}
                  >
                    {formatTime(timeRemaining)}
                  </Text>
                )}
              </Box>
            </Group>
          </Paper>

          <Text ta={"right"} size="xs" c={"dimmed"} w={"100%"}>
            {new Date().toLocaleString(lang.id === "FRA" ? "fr-FR" : "en-EN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
            })}
          </Text>
        </Stack>
      </Group>
    </Stack>
  );
};

export default MessageVocalPreviewer;
